import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { medicamentoService } from '../services';
import { Save, ArrowLeft, AlertCircle, Package } from 'lucide-react';

const CadastroMedicamento = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    nome: '',
    principioAtivo: '',
    fabricante: '',
    codigoBarras: '',
    dosagem: '',
    formaFarmaceutica: '',
    categoria: '',
    tipoControle: 'sem_controle',
    estoque: '',
    estoqueMinimo: '10',
    dataValidade: '',
    lote: '',
    numeroNotaFiscal: '',
    prescricaoObrigatoria: false
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [cadastroRapido, setCadastroRapido] = useState(false);
  const [medicamentoRegistrado, setMedicamentoRegistrado] = useState(null);
  const [buscandoCodigo, setBuscandoCodigo] = useState(false);

  const categorias = [
    'analgesico',
    'antibiotico',
    'antinflamatorio',
    'cardiovascular',
    'dermatologico',
    'gastrointestinal',
    'neurologico',
    'psiquiatrico',
    'respiratorio',
    'vitaminas',
    'outros'
  ];

  const categoriasLabels = {
    'analgesico': 'Analgésico',
    'antibiotico': 'Antibiótico',
    'antinflamatorio': 'Anti-inflamatório',
    'cardiovascular': 'Cardiovascular',
    'dermatologico': 'Dermatológico',
    'gastrointestinal': 'Gastrointestinal',
    'neurologico': 'Neurológico',
    'psiquiatrico': 'Psiquiátrico',
    'respiratorio': 'Respiratório',
    'vitaminas': 'Vitaminas',
    'outros': 'Outros'
  };

  const formasFarmaceuticas = [
    'Aerosol',
    'Ampola',
    'Bolsa',
    'Cápsula',
    'Comprimido',
    'Creme',
    'Envelope/Pó',
    'Flaconete',
    'Gotas',
    'Gel',
    'Injetável',
    'Preenchida',
    'Pomada',
    'Spray',
    'Solução',
    'Suspensão',
    'Unidade',
    'Xarope',
    '==//=='
  ];

  const buscarPorCodigoBarras = async (codigoBarras) => {
    if (!codigoBarras || codigoBarras.length < 3) return;
    
    setBuscandoCodigo(true);
    try {
      const medicamento = await medicamentoService.buscarPorCodigoBarras(codigoBarras);
      
      // Medicamento registrado encontrado - modo cadastro rápido
      setMedicamentoRegistrado(medicamento);
      setCadastroRapido(true);
      
      setFormData({
        // Campos preenchidos automaticamente (readonly no modo rápido)
        nome: medicamento.nome || '',
        principioAtivo: medicamento.principioAtivo || '',
        codigoBarras: medicamento.codigoBarras || '',
        dosagem: medicamento.dosagem || '',
        formaFarmaceutica: medicamento.formaFarmaceutica || '',
        categoria: medicamento.categoria || '',
        tipoControle: medicamento.tipoControle || 'sem_controle',
        prescricaoObrigatoria: medicamento.prescricaoObrigatoria || false,
        
        // Campos que o usuário ainda precisa preencher
        fabricante: '',
        estoque: '',
        estoqueMinimo: '10',
        dataValidade: '',
        lote: '',
        numeroNotaFiscal: ''
      });
      
      setMessage({ 
        type: 'success', 
        text: `Medicamento ${medicamento.nome} encontrado! Preencha os dados restantes do estoque.` 
      });
      
    } catch (error) {
      // Medicamento não encontrado - modo normal
      setCadastroRapido(false);
      setMedicamentoRegistrado(null);
      
      if (error.response?.status === 404) {
        setMessage({ 
          type: 'info', 
          text: 'Medicamento não encontrado. Preencha todos os dados para cadastrar um novo medicamento.' 
        });
      } else {
        console.error('Erro ao buscar medicamento:', error);
        setMessage({ 
          type: 'error', 
          text: 'Erro ao buscar medicamento. Tente novamente.' 
        });
      }
    } finally {
      setBuscandoCodigo(false);
    }
  };

  // Detectar quando código de barras é alterado
  const handleCodigoBarrasChange = (e) => {
    const codigo = e.target.value;
    setFormData({ ...formData, codigoBarras: codigo });
    
    // Se não estamos editando e o código tem tamanho suficiente
    if (!isEditing && codigo && codigo.length >= 8) {
      buscarPorCodigoBarras(codigo);
    }
  };

  // Reset do modo rápido - removido, agora usamos limparCampos

  const limparCampos = () => {
    setFormData({
      nome: '',
      principioAtivo: '',
      fabricante: '',
      codigoBarras: '',
      dosagem: '',
      formaFarmaceutica: '',
      categoria: '',
      tipoControle: 'sem_controle',
      estoque: '',
      estoqueMinimo: '10',
      dataValidade: '',
      lote: '',
      numeroNotaFiscal: '',
      prescricaoObrigatoria: false
    });
    setCadastroRapido(false);
    setMedicamentoRegistrado(null);
    setMessage({ type: '', text: '' });
  };

  const carregarMedicamento = useCallback(async () => {
    try {
      setLoading(true);
      const item = await medicamentoService.getById(id);
      
      // Determinar se é item de estoque ou medicamento registrado
      const medicamento = item.medicamentoRegistrado || item;
      
      // Formatar data para input
      const dataValidade = new Date(item.dataValidade || medicamento.dataValidade).toISOString().split('T')[0];
      
      setFormData({
        // Campos do medicamento registrado (somente leitura na edição)
        nome: medicamento.nome || '',
        principioAtivo: medicamento.principioAtivo || '',
        codigoBarras: medicamento.codigoBarras || '',
        dosagem: medicamento.dosagem || '',
        formaFarmaceutica: medicamento.formaFarmaceutica || '',
        categoria: medicamento.categoria || '',
        tipoControle: medicamento.tipoControle || 'sem_controle',
        prescricaoObrigatoria: medicamento.prescricaoObrigatoria || false,
        
        // Campos do item de estoque (editáveis)
        fabricante: item.fabricante || '',
        estoque: item.estoque || '',
        estoqueMinimo: item.estoqueMinimo || '10',
        dataValidade: dataValidade,
        lote: item.lote || '',
        numeroNotaFiscal: item.numeroNotaFiscal || ''
      });
    } catch (error) {
      console.error('Erro ao carregar medicamento:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados do medicamento' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Verificar permissões
    if (user.cargo !== 'admin' && user.cargo !== 'farmaceutico') {
      navigate('/medicamentos');
      return;
    }

    if (isEditing) {
      carregarMedicamento();
    }
  }, [isEditing, user.cargo, navigate, carregarMedicamento]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpar mensagem de erro
    if (message.text) setMessage({ type: '', text: '' });
  };

  const validateForm = () => {
    // No modo cadastro rápido, apenas validar campos editáveis
    // No modo de edição, validar apenas campos editáveis
    // No modo normal, validar todos os campos obrigatórios
    const required = cadastroRapido ? 
      ['fabricante', 'dataValidade'] : 
      isEditing ? 
        ['fabricante', 'dataValidade'] :
        ['nome', 'principioAtivo', 'fabricante', 'dataValidade'];
    
    const missing = required.filter(field => !formData[field]);
    
    if (missing.length > 0) {
      const fieldNames = {
        nome: 'nome',
        principioAtivo: 'princípio ativo', 
        fabricante: 'fabricante',
        dataValidade: 'data de validade'
      };
      const translatedMissing = missing.map(field => fieldNames[field] || field);
      setMessage({ 
        type: 'error', 
        text: 'Preencha todos os campos obrigatórios: ' + translatedMissing.join(', ')
      });
      return false;
    }

    if (parseInt(formData.estoque) < 0) {
      setMessage({ type: 'error', text: 'O estoque não pode ser negativo' });
      return false;
    }

    if (parseInt(formData.estoqueMinimo) < 0) {
      setMessage({ type: 'error', text: 'O estoque mínimo não pode ser negativo' });
      return false;
    }

    const hoje = new Date();
    const validade = new Date(formData.dataValidade);
    if (validade < hoje) {
      setMessage({ type: 'error', text: 'A data de validade não pode ser anterior à data atual' });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      if (cadastroRapido) {
        // Cadastro rápido - apenas adicionar item ao estoque
        const novoItem = {
          medicamentoRegistradoId: medicamentoRegistrado.id,
          fabricante: formData.fabricante,
          estoque: parseInt(formData.estoque) || 0,
          estoqueMinimo: parseInt(formData.estoqueMinimo) || 10,
          dataValidade: formData.dataValidade,
          lote: formData.lote,
          numeroNotaFiscal: formData.numeroNotaFiscal
        };
        
        await medicamentoService.create(novoItem);
        setMessage({ type: 'success', text: 'Item adicionado ao estoque com sucesso!' });
        
      } else {
        // Cadastro normal ou edição
        const dadosEnvio = {
          ...formData,
          estoque: parseInt(formData.estoque) || 0,
          estoqueMinimo: parseInt(formData.estoqueMinimo) || 10
        };

        // No modo de edição, enviar apenas campos do estoque
        if (isEditing) {
          const dadosEstoque = {
            fabricante: dadosEnvio.fabricante,
            estoque: dadosEnvio.estoque,
            estoqueMinimo: dadosEnvio.estoqueMinimo,
            dataValidade: dadosEnvio.dataValidade,
            lote: dadosEnvio.lote,
            numeroNotaFiscal: dadosEnvio.numeroNotaFiscal
          };
          await medicamentoService.update(id, dadosEstoque);
          setMessage({ type: 'success', text: 'Item de estoque atualizado com sucesso!' });
        } else {
          await medicamentoService.create(dadosEnvio);
          setMessage({ type: 'success', text: 'Medicamento cadastrado com sucesso!' });
        }
      }

      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/medicamentos');
      }, 2000);

    } catch (error) {
      console.error('Erro ao salvar medicamento:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erro ao salvar medicamento'
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '30px' }}>
        <button 
          onClick={() => navigate('/medicamentos')}
          className="btn btn-secondary"
          style={{ marginBottom: '15px' }}
        >
          <ArrowLeft size={20} />
          Voltar
        </button>
        
        <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Package size={32} />
          {isEditing ? 'Editar Medicamento' : cadastroRapido ? 'Cadastro Rápido -' : 'Novo Medicamento'}
          {cadastroRapido && (
            <span style={{ fontSize: '18px', fontWeight: 'normal', color: '#667' }}>
              {medicamentoRegistrado?.nome}
            </span>
          )}
        </h1>
        <p style={{ color: '#667', margin: '5px 0 0 0' }}>
          {isEditing ? 'Atualize as informações do medicamento' : 'Cadastre um novo medicamento no sistema'}
        </p>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      {isEditing && (
        <div className="card" style={{ marginBottom: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
          <h4 style={{ color: '#495057', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            📝 Modo de Edição - Item de Estoque
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
            <div>
              <strong>🔒 Campos Bloqueados:</strong>
              <ul style={{ margin: '5px 0', paddingLeft: '20px', color: '#667' }}>
                <li>Informações do medicamento (nome, princípio ativo, etc.)</li>
                <li>Especificações técnicas (dosagem, forma farmacêutica)</li>
                <li>Classificações (categoria, tipo de controle)</li>
              </ul>
            </div>
            <div>
              <strong>✏️ Campos Editáveis:</strong>
              <ul style={{ margin: '5px 0', paddingLeft: '20px', color: '#28a745' }}>
                <li>Fabricante do item específico</li>
                <li>Quantidades de estoque</li>
                <li>Data de validade e lote</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            
            {/* Informações Básicas */}
            <div>
              <h3 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                Informações Básicas
              </h3>
              
              <div className="form-group">
                <label className="form-label">Nome do Medicamento *</label>
                <input
                  type="text"
                  name="nome"
                  className="form-control"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Paracetamol 500mg"
                  disabled={isEditing || cadastroRapido}
                  style={isEditing || cadastroRapido ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                />
                {(isEditing || cadastroRapido) && (
                  <small style={{ color: '#667', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    🔒 Campo não editável - dados do medicamento registrado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Princípio Ativo *</label>
                <input
                  type="text"
                  name="principioAtivo"
                  className="form-control"
                  value={formData.principioAtivo}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Paracetamol"
                  disabled={isEditing || cadastroRapido}
                  style={isEditing || cadastroRapido ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                />
                {(isEditing || cadastroRapido) && (
                  <small style={{ color: '#667', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    🔒 Campo não editável - dados do medicamento registrado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Fabricante *</label>
                <input
                  type="text"
                  name="fabricante"
                  className="form-control"
                  value={formData.fabricante}
                  onChange={handleChange}
                  placeholder="Ex: EMS, Medley, Eurofarma"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Código de Barras
                  {!isEditing && (
                    <span style={{ fontSize: '12px', color: '#667', marginLeft: '8px' }}>
                      (Digite para busca automática)
                    </span>
                  )}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    name="codigoBarras"
                    className="form-control"
                    value={formData.codigoBarras}
                    onChange={isEditing ? handleChange : handleCodigoBarrasChange}
                    placeholder="Ex: 7891234567890"
                    disabled={isEditing || (cadastroRapido && medicamentoRegistrado)}
                    style={{
                      backgroundColor: isEditing || (cadastroRapido && medicamentoRegistrado) ? '#f5f5f5' : 'white',
                      cursor: isEditing || (cadastroRapido && medicamentoRegistrado) ? 'not-allowed' : 'text',
                      paddingRight: buscandoCodigo ? '40px' : '12px'
                    }}
                  />
                  {buscandoCodigo && (
                    <div style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#667'
                    }}>
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="sr-only">Buscando...</span>
                      </div>
                    </div>
                  )}
                </div>
                <small style={{ color: '#667', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                  {isEditing ? 
                    '🔒 Campo não editável - dados do medicamento registrado' :
                    '💡 Múltiplos medicamentos podem ter o mesmo código de barras (diferentes lotes/validades)'
                  }
                </small>
              </div>
            </div>

            {/* Especificações */}
            <div>
              <h3 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                Especificações
              </h3>

              <div className="form-group">
                <label className="form-label">Dosagem</label>
                <input
                  type="text"
                  name="dosagem"
                  className="form-control"
                  value={formData.dosagem}
                  onChange={handleChange}
                  placeholder="Ex: 500mg, 10ml, 250mg/5ml"
                  disabled={isEditing || cadastroRapido}
                  style={isEditing || cadastroRapido ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                />
                {(isEditing || cadastroRapido) && (
                  <small style={{ color: '#667', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    🔒 Campo não editável - dados do medicamento registrado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Forma Farmacêutica</label>
                <select
                  name="formaFarmaceutica"
                  className="form-control"
                  value={formData.formaFarmaceutica}
                  onChange={handleChange}
                  disabled={isEditing || cadastroRapido}
                  style={isEditing || cadastroRapido ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                >
                  <option value="">Selecione...</option>
                  {formasFarmaceuticas.map(forma => (
                    <option key={forma} value={forma}>{forma}</option>
                  ))}
                </select>
                {(isEditing || cadastroRapido) && (
                  <small style={{ color: '#667', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    🔒 Campo não editável - dados do medicamento registrado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select
                  name="categoria"
                  className="form-control"
                  value={formData.categoria}
                  onChange={handleChange}
                  disabled={isEditing || cadastroRapido}
                  style={isEditing || cadastroRapido ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                >
                  <option value="">Selecione...</option>
                  {categorias.map(cat => (
                    <option key={cat} value={cat}>{categoriasLabels[cat]}</option>
                  ))}
                </select>
                {(isEditing || cadastroRapido) && (
                  <small style={{ color: '#667', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    🔒 Campo não editável - dados do medicamento registrado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Controle</label>
                <select
                  name="tipoControle"
                  className="form-control"
                  value={formData.tipoControle}
                  onChange={handleChange}
                  disabled={isEditing || cadastroRapido}
                  style={isEditing || cadastroRapido ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                >
                  <option value="sem_controle">Sem Controle</option>
                  <option value="normal">Controle Normal</option>
                  <option value="especial">Controle Especial</option>
                </select>
                {(isEditing || cadastroRapido) && (
                  <small style={{ color: '#667', fontSize: '12px', marginTop: '5px', display: 'block' }}>
                    🔒 Campo não editável - dados do medicamento registrado
                  </small>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    name="prescricaoObrigatoria"
                    checked={formData.prescricaoObrigatoria}
                    onChange={handleChange}
                    style={{ marginRight: '8px' }}
                    disabled={isEditing || cadastroRapido}
                  />
                  Prescrição obrigatória
                  {(isEditing || cadastroRapido) && (
                    <span style={{ color: '#667', fontSize: '12px', marginLeft: '10px' }}>
                      🔒 Não editável
                    </span>
                  )}
                </label>
              </div>
            </div>

            {/* Estoque */}
            <div>
              <h3 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                Estoque
              </h3>

              <div className="form-group">
                <label className="form-label">Estoque Atual</label>
                <input
                  type="number"
                  name="estoque"
                  className="form-control"
                  value={formData.estoque}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Estoque Mínimo</label>
                <input
                  type="number"
                  name="estoqueMinimo"
                  className="form-control"
                  value={formData.estoqueMinimo}
                  onChange={handleChange}
                  placeholder="10"
                  min="0"
                />
              </div>
            </div>

            {/* Validade e Lote */}
            <div>
              <h3 style={{ marginBottom: '20px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                Validade e Lote
              </h3>

              <div className="form-group">
                <label className="form-label">Data de Validade *</label>
                <input
                  type="date"
                  name="dataValidade"
                  className="form-control"
                  value={formData.dataValidade}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Lote</label>
                <input
                  type="text"
                  name="lote"
                  className="form-control"
                  value={formData.lote}
                  onChange={handleChange}
                  placeholder="Ex: L001234"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Número da Nota Fiscal</label>
                <input
                  type="text"
                  name="numeroNotaFiscal"
                  className="form-control"
                  value={formData.numeroNotaFiscal}
                  onChange={handleChange}
                  onInput={(e) => {
                    // Filtrar apenas números
                    e.target.value = e.target.value.replace(/[^0-9]/g, '');
                  }}
                  placeholder="Ex: 123456"
                />
              </div>
            </div>
          </div>

          {/* Botões */}
          <div style={{ 
            marginTop: '30px', 
            paddingTop: '20px', 
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '15px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={limparCampos}
              className="btn btn-danger"
              disabled={loading}
              style={{ marginRight: 'auto' }}
            >
              Limpar Campos
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/medicamentos')}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px', margin: '0' }}></div>
                  {isEditing ? 'Atualizando...' : 'Cadastrando...'}
                </>
              ) : (
                <>
                  <Save size={20} />
                  {isEditing ? 'Atualizar' : 'Cadastrar'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadastroMedicamento;